package com.macrofocus.eflux.ui

import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.selection.MutableSelection
import com.macrofocus.eflux.context.ActiveTooltip
import com.macrofocus.eflux.network.EFluxLink
import com.macrofocus.eflux.network.EFluxNetwork
import com.macrofocus.eflux.network.EFluxNode
import com.macrofocus.eflux.network.GraphicalNetwork
import com.macrofocus.eflux.network.logical.LogicalBranchId
import com.macrofocus.eflux.network.logical.LogicalBusId
import org.mkui.interaction.InteractionMode
import web.geometry.DOMPoint
import web.mouse.MouseEvent
import web.svg.*

class Controller(private val activeTooltip: MutableProperty<ActiveTooltip>) {
    fun processMouseSelection(
        interactionMode: InteractionMode,
        eFluxNetwork: EFluxNetwork,
        it: MouseEvent,
        graphicalNetwork: GraphicalNetwork,
        pointProbing: MutableSelection<LogicalBusId>,
        pathProbing: MutableSelection<LogicalBranchId>
    ): Any {
        val closestNode: EFluxNode? = if(activeTooltip.value.nodes) findPoint(eFluxNetwork, it) else null

        if (closestNode != null) {
            when (interactionMode) {
                InteractionMode.Selection -> {
                    pointProbing.setSelectedElement(closestNode.id)
                }
                InteractionMode.Toggle -> {
//                    println("Toggle: ${it.x},${it.y} -> ${closestNode.anchorSVG.x},${closestNode.anchorSVG.y} -> ${closestNode.svgToScreen(closestNode.anchorSVG).x},${closestNode.svgToScreen(closestNode.anchorSVG).y}")
                    val e_box = closestNode.boundingClientRect
                    val svg_box = graphicalNetwork.svg.getBBox();
                    val x = e_box.x  / svg_box.width
                    val y = e_box.y / svg_box.height
//                    println("Result $x,$y")
                    pointProbing.setSelectedState(closestNode.id, !pointProbing.isSelected(closestNode.id))
                }
                else -> {}
            }
        } else {
            when (interactionMode) {
                InteractionMode.Selection -> {
                    pointProbing.clearSelection()
                }
                else -> {}
            }
        }

        return if (closestNode == null) {
            val closestLink: EFluxLink? = if(activeTooltip.value.links) findClosestPath(eFluxNetwork, it) else null

            if (closestLink != null) {
                when (interactionMode) {
                    InteractionMode.Selection -> {
                        pathProbing.setSelectedElement(closestLink.id)
                    }
                    InteractionMode.Toggle -> {
                        pathProbing.setSelectedState(closestLink.id, !pathProbing.isSelected(closestLink.id))
                    }
                    else -> {}
                }
            } else {
                when (interactionMode) {
                    InteractionMode.Selection -> {
                        pathProbing.clearSelection()
                    }
                    InteractionMode.Toggle -> {
                    }
                    else -> {}
                }
            }
        } else {
            when (interactionMode) {
                InteractionMode.Selection -> {
                    pathProbing.clearSelection()
                }
                else -> {}
            }
        }
    }

    private fun svgToPoint(
        netSchema: EFluxNetwork,
        p: DOMPoint
    ): DOMPoint {
        val svgP :DOMPoint
        svgP = if (USE_OFFSET) {
            p.matrixTransform(netSchema.pointsParent.getCTM()!!)
        } else {
            p.matrixTransform(netSchema.pointsParent.getScreenCTM()!!)
        }
        return svgP
    }

    public fun mouseToSVG(
        netSchema: EFluxNetwork,
        it: MouseEvent
    ): DOMPoint {
        val p = netSchema.svg.createSVGPoint()
        val svgP: DOMPoint
        if (USE_OFFSET) {
            p.x = it.offsetX
            p.y = it.offsetY
            svgP = p.matrixTransform(netSchema.pointsParent.getCTM()!!.inverse())
        } else {
            p.x = it.x
            p.y = it.y
            svgP = p.matrixTransform(netSchema.pointsParent.getScreenCTM()!!.inverse())
        }
        return svgP
    }

    fun findPoint(
        netSchema: EFluxNetwork,
        it: MouseEvent
    ): EFluxNode? {
        val svgP: DOMPoint = mouseToSVG(netSchema, it)

        for (point in netSchema.nodes) {
            for (element in point.point.elements) {
                if(element is SVGGeometryElement) {
                    if(element.isPointInFill(svgP) || element.isPointInStroke(svgP)) {
                        return point
                    }
                } else {
                    println("Element $element is not of type SVGGeometryElement")
                }
            }
        }
        return null
    }

    private fun findClosestPath(
        network: EFluxNetwork,
        it: MouseEvent
    ): EFluxLink? {
        val svgP: DOMPoint = mouseToSVG(network, it)

        val x1 = svgP.x
        val y1 = svgP.y

        var closest: EFluxLink? = null
        var bestDist: Double? = null
        for (link in network.links) {
            val elements = link.path.elements
            for (element in elements) {
                val dist = computeDistance(element, x1, y1, link)
                if(bestDist == null || (dist != null && dist < bestDist)) {
                    bestDist = dist
                    closest = link
                }
            }
        }

        return if(bestDist != null && bestDist <= 8.0) {
            closest
        } else {
            null
        }
    }

    private fun computeDistance(
        element: SVGElement,
        x1: Double,
        y1: Double,
        link: EFluxLink
    ): Double? {
        if (element is SVGPolylineElement) {
            var bestDist:Double? = null

            val p = element.points
            var p2 = p.getItem(0)
            for (i in 1 until p.length) {
                val p1 = p2
                p2 = p.getItem(i)

                val dist = distanceToLine(x1, y1, p1.x, p1.y, p2.x, p2.y)

                if (bestDist == null) {
                    bestDist = dist
                } else {
                    if (dist < bestDist) {
                        bestDist = dist
                    }
                }
            }

            return bestDist
        } else if (element is SVGPathElement) {
            val pathLength: Float = element.getTotalLength().toFloat()
            var precision = 8.0f
            var best: DOMPoint? = null
            var bestLength: Float = Float.POSITIVE_INFINITY
            var bestDistance: Double = Double.POSITIVE_INFINITY

            // linear scan for coarse approximation
            var scanLength = 0f
            while (scanLength <= pathLength) {
                val scan = element.getPointAtLength(scanLength)
                val scanDistance = distanceToPoint(scan.x, scan.y, x1, y1)
                if (scanDistance < bestDistance) {
                    best = scan
                    bestLength = scanLength
                    bestDistance = scanDistance
                }
                scanLength += precision
            }

            // binary search for precise estimate
            precision /= 2
            while (precision > 0.5) {
                val beforeLength = bestLength - precision
                val before = element.getPointAtLength(beforeLength)
                val beforeDistance = distanceToPoint(before.x, before.y, x1, y1)

                val afterLength = bestLength + precision
                val after = element.getPointAtLength(afterLength)
                val afterDistance = distanceToPoint(after.x, after.y, x1, y1)

                if (beforeLength >= 0 && beforeDistance < bestDistance) {
                    best = before
                    bestLength = beforeLength
                    bestDistance = beforeDistance
                } else if (afterLength <= pathLength && afterDistance < bestDistance) {
                    best = after
                    bestLength = afterLength
                    bestDistance = afterDistance
                } else {
                    precision /= 2
                }
            }

            return bestDistance
        } else if (element is SVGLineElement) {
            val p1 = element.getPointAtLength(0f)
            val p2 = element.getPointAtLength(element.getTotalLength())

            return distanceToLine(x1, y1, p1.x, p1.y, p2.x, p2.y)
        } else {
            var bestDist:Double? = null

            if (element.childElementCount > 0) {
                for (i in 0 until element.childElementCount) {
                    val p2 = element.children[i]

                    if(p2 is SVGElement) {
                        val dist = computeDistance(p2, x1, y1, link)

                        if (bestDist == null) {
                            bestDist = dist
                        } else {
                            if (dist != null && dist < bestDist) {
                                bestDist = dist
                            }
                        }
                    } else {
                        println("Unknown element type " + p2 + ": " + p2?.tagName)
                    }
                }
            } else {
                println("Unknown type " + element.tagName)
            }
            return bestDist
        }
    }

    private fun distanceToPoint(x: Double, y: Double, x1: Double, y1: Double): Double {
        val dx = x1 - x
        val dy = y1 - y
        return dy * dy + dx * dx
    }

    private fun distanceToLine(x: Double, y: Double, x1: Double, y1: Double, x2: Double, y2: Double): Double {
        val A = x - x1
        val B = y - y1
        val C = x2 - x1
        val D = y2 - y1
        val dot = A * C + B * D
        val len_sq = C * C + D * D
        var param = -1.0
        if (len_sq != 0.0) //in case of 0 length line
            param = dot / len_sq
        val xx: Double
        val yy: Double
        if (param < 0) {
            xx = x1
            yy = y1
        } else if (param > 1) {
            xx = x2
            yy = y2
        } else {
            xx = x1 + param * C
            yy = y1 + param * D
        }
        val dx = x - xx
        val dy = y - yy
        return dx * dx + dy * dy
    }
}